'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Search } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Container } from '@/components/utils/Container';
import { PaginatorBar } from '@/components/ui/navigation';
import { Alert, AlertDescription } from '@/components/ui/feedback/alert';
import { Button } from '@/components/ui/forms/button';
import { SectionHeader } from '@/components/ui/design-system/SectionHeader';
import { SectionPanel } from '@/components/ui/design-system';
import MainLoader from '@/components/ui/feedback/MainLoader';
import { ViewModeToggle, type ViewMode } from '@/app/dashboard/components/ViewModeToggle';
import { JobsList } from '@/components/features/jobs/JobsList';
import type { Job, JobSearchParams, JobsListResponse } from '@/types/jobs-types';
import { JobsLookupSearchPanel } from '@/components/features/jobs/JobsLookupSearchPanel';
import { isSearchConfirmShortcut } from '@/lib/keyboard-shortcuts';
import { useSearchProgress } from '@/hooks/useSearchProgress';

interface JobsSearchExperienceProps {
  basePath: string;
  recommendedPath: string;
  recommendedLabel?: string;
  exploreLabel?: string;
  showTopTabs?: boolean;
}

const JOBS_PER_PAGE = 10;

const normalizePage = (value?: number): number => {
  if (!Number.isFinite(value) || !value || value < 1) {
    return 1;
  }
  return Math.floor(value);
};

const sanitizeCompanies = (companies?: string[]): string[] | undefined => {
  if (!companies) return undefined;

  const cleaned = companies
    .map((company) => company.trim())
    .filter(Boolean);

  return cleaned.length > 0 ? cleaned : undefined;
};

const sanitizeTitles = (titles?: string[]): string[] | undefined => {
  if (!titles) return undefined;

  const cleaned = titles
    .map((title) => title.trim())
    .filter(Boolean);

  return cleaned.length > 0 ? cleaned : undefined;
};

const isAbortError = (error: unknown) => {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  return error instanceof Error && error.name === 'AbortError';
};

export function JobsSearchExperience({
  basePath,
  recommendedPath,
  recommendedLabel = 'RECOMMENDED',
  exploreLabel = 'Explore Jobs',
  showTopTabs = false,
}: JobsSearchExperienceProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchTitles, setSearchTitles] = useState<string[]>([]);
  const [titleInput, setTitleInput] = useState('');
  const [searchCompanies, setSearchCompanies] = useState<string[]>([]);
  const [companyInput, setCompanyInput] = useState('');
  const [searchResults, setSearchResults] = useState<Job[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [pendingAutoSearch, setPendingAutoSearch] = useState<{
    title?: string;
    location?: string;
    titles?: string[];
    companies?: string[];
    page?: number;
  } | null>(null);

  const restoredSearchKeyRef = useRef<string | null>(null);
  const searchResultsTopRef = useRef<HTMLDivElement | null>(null);
  const pendingSearchPageScrollRef = useRef<number | null>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const searchRequestIdRef = useRef(0);
  const searchProgress = useSearchProgress(isSearchLoading);

  const addCompany = useCallback(() => {
    const value = companyInput.trim();
    if (
      value &&
      searchCompanies.length < 5 &&
      !searchCompanies.some((candidate) => candidate.toLowerCase() === value.toLowerCase())
    ) {
      setSearchCompanies([...searchCompanies, value]);
      setCompanyInput('');
    }
  }, [companyInput, searchCompanies]);

  const addTitle = useCallback(() => {
    const value = titleInput.trim();
    if (
      value &&
      searchTitles.length < 5 &&
      !searchTitles.some((candidate) => candidate.toLowerCase() === value.toLowerCase())
    ) {
      setSearchTitles([...searchTitles, value]);
      setTitleInput('');
    }
  }, [searchTitles, titleInput]);

  const getSearchTitlesWithPendingInput = useCallback(() => {
    const value = titleInput.trim();
    if (!value) {
      return searchTitles;
    }
    if (searchTitles.some((candidate) => candidate.toLowerCase() === value.toLowerCase())) {
      return searchTitles;
    }
    if (searchTitles.length >= 5) {
      return searchTitles;
    }
    return [...searchTitles, value];
  }, [searchTitles, titleInput]);

  const getSearchCompaniesWithPendingInput = useCallback(() => {
    const value = companyInput.trim();
    if (!value) {
      return searchCompanies;
    }
    if (searchCompanies.some((candidate) => candidate.toLowerCase() === value.toLowerCase())) {
      return searchCompanies;
    }
    if (searchCompanies.length >= 5) {
      return searchCompanies;
    }
    return [...searchCompanies, value];
  }, [companyInput, searchCompanies]);

  const cancelSearch = useCallback(() => {
    searchRequestIdRef.current += 1;
    searchAbortControllerRef.current?.abort();
    searchAbortControllerRef.current = null;
    setSearchError(null);
    setIsSearchLoading(false);
  }, []);

  useEffect(() => () => {
    searchAbortControllerRef.current?.abort();
  }, []);

  const executeSearch = useCallback(async (params: {
    title?: string;
    location?: string;
    titles?: string[];
    companies?: string[];
    page?: number;
  }, options: { syncUrl?: boolean } = {}) => {
    searchAbortControllerRef.current?.abort();
    const controller = new AbortController();
    searchAbortControllerRef.current = controller;
    const requestId = ++searchRequestIdRef.current;

    setIsSearchLoading(true);
    setSearchError(null);

    const normalizedPage = normalizePage(params.page || 1);
    const titles = sanitizeTitles(params.titles);
    const companies = sanitizeCompanies(params.companies);

    if (options.syncUrl !== false) {
      const urlParams = new URLSearchParams();
      if (params.title?.trim()) urlParams.set('title', params.title.trim());
      if (params.location?.trim()) urlParams.set('location', params.location.trim());
      if (titles?.length) urlParams.set('titles', titles.join(','));
      if (companies?.length) urlParams.set('companies', companies.join(','));
      urlParams.set('page', normalizedPage.toString());

      const queryString = urlParams.toString();
      router.replace(queryString ? `${basePath}?${queryString}` : basePath, { scroll: true });
    }

    const payload: JobSearchParams = {
      title: params.title?.trim() || undefined,
      titles,
      location: params.location?.trim() || undefined,
      companies,
      page: normalizedPage,
      limit: JOBS_PER_PAGE,
      // mode disabled for now (use backend default behavior)
      // mode: 'expand_keywords_internal_vector_paid_shuffle_organic'
    };

    try {
      const response = await fetch('/api/dashboard/jobs/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        cache: 'no-store',
        signal: controller.signal,
      });

      const json = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(json?.error || 'Search failed');
      }

      if (requestId !== searchRequestIdRef.current) {
        return;
      }

      const jobsResponse = json as JobsListResponse;
      setSearchResults(jobsResponse.results || []);
      setSearchTotal(jobsResponse.total || 0);
      setSearchPage(normalizedPage);
    } catch (err) {
      if (controller.signal.aborted || isAbortError(err)) {
        return;
      }
      if (requestId !== searchRequestIdRef.current) {
        return;
      }
      setSearchError(err instanceof Error ? err.message : 'Search failed');
      setSearchResults([]);
      setSearchTotal(0);
    } finally {
      if (searchAbortControllerRef.current === controller) {
        searchAbortControllerRef.current = null;
      }
      if (requestId === searchRequestIdRef.current) {
        setIsSearchLoading(false);
      }
    }
  }, [basePath, router]);

  useEffect(() => {
    const searchKey = searchParams.toString();
    if (restoredSearchKeyRef.current === searchKey) return;
    restoredSearchKeyRef.current = searchKey;

    const keyword = searchParams.get('title') || searchParams.get('keyword');
    const location = searchParams.get('location');
    const titles = searchParams.get('titles');
    const companies = searchParams.get('companies');
    const page = searchParams.get('page');
    const restoredPage = normalizePage(page ? Number(page) : 1);

    setSearchKeyword(keyword || '');
    setSearchLocation(location || '');
    const parsedTitles = titles
      ? titles
          .split(',')
          .map((title) => title.trim())
          .filter(Boolean)
      : [];
    setSearchTitles(parsedTitles);
    const parsedCompanies = companies
      ? companies
          .split(',')
          .map((company) => company.trim())
          .filter(Boolean)
      : [];
    setSearchCompanies(parsedCompanies);

    if (keyword || location || parsedTitles.length > 0 || parsedCompanies.length > 0) {
      setPendingAutoSearch({
        title: keyword || undefined,
        location: location || undefined,
        titles: sanitizeTitles(parsedTitles),
        companies: sanitizeCompanies(parsedCompanies),
        page: restoredPage,
      });
      return;
    }

    cancelSearch();
    setPendingAutoSearch(null);
    setSearchResults([]);
    setSearchTotal(0);
    setSearchPage(1);
    setSearchError(null);
  }, [cancelSearch, executeSearch, searchParams]);

  // Execute URL-restored search through a dedicated queue to avoid mount-time races.
  useEffect(() => {
    if (!pendingAutoSearch) {
      return;
    }
    setPendingAutoSearch(null);
    void executeSearch(pendingAutoSearch, { syncUrl: false });
  }, [executeSearch, pendingAutoSearch]);

  const handleSearch = useCallback(async (params: {
    title?: string;
    location?: string;
    titles?: string[];
    companies?: string[];
    page?: number;
  }) => {
    await executeSearch(params);
  }, [executeSearch]);

  const handleSearchPageChange = useCallback((nextPage: number) => {
    const normalizedPage = normalizePage(nextPage);
    pendingSearchPageScrollRef.current = normalizedPage;
    void handleSearch({
      title: searchKeyword,
      location: searchLocation,
      titles: searchTitles,
      companies: searchCompanies,
      page: normalizedPage,
    });
  }, [handleSearch, searchCompanies, searchKeyword, searchLocation, searchTitles]);

  const handleLookupSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const titlesToSearch = getSearchTitlesWithPendingInput();
    const companiesToSearch = getSearchCompaniesWithPendingInput();
    setSearchTitles(titlesToSearch);
    setSearchCompanies(companiesToSearch);
    if (titleInput.trim()) {
      setTitleInput('');
    }
    if (companyInput.trim()) {
      setCompanyInput('');
    }
    void handleSearch({
      title: searchKeyword,
      location: searchLocation,
      titles: titlesToSearch,
      companies: companiesToSearch,
      page: 1
    });
  }, [
    companyInput,
    getSearchCompaniesWithPendingInput,
    getSearchTitlesWithPendingInput,
    handleSearch,
    searchKeyword,
    searchLocation,
    titleInput,
  ]);

  useEffect(() => {
    if (pendingSearchPageScrollRef.current === null) return;
    if (isSearchLoading) return;
    if (searchPage !== pendingSearchPageScrollRef.current) return;

    pendingSearchPageScrollRef.current = null;
    requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant',
      });
    });
  }, [isSearchLoading, searchPage]);

  const isAuthenticatedApp = basePath.startsWith('/dashboard') || basePath.startsWith('/d');
  const detailBasePath = isAuthenticatedApp ? '/d/job' : '/job';
  const shouldShowPagination = searchPage > 1 || searchResults.length > 0;
  const jobsCountLabel = searchTotal > 0 ? searchTotal : searchResults.length;

  return (
    <div className="min-h-screen bg-background">
      <Container className="pb-8">
        {showTopTabs && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="py-3 border-b border-border mb-3">
            <div className="flex items-center justify-between gap-6">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Button
                  className="h-9 px-4 text-xs font-bold uppercase tracking-wide bg-primary text-primary-foreground"
                  size="sm"
                >
                  <Search className="h-3 w-3 mr-1" />
                  {exploreLabel}
                </Button>
                <Button
                  variant="outline"
                  className="border border-border h-9 px-3 text-xs font-bold uppercase tracking-wide"
                  size="sm"
                  onClick={() => router.push(recommendedPath)}
                >
                  {recommendedLabel}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-4"
        >
          <JobsLookupSearchPanel
            searchKeyword={searchKeyword}
            onSearchKeywordChange={setSearchKeyword}
            searchLocation={searchLocation}
            onSearchLocationChange={setSearchLocation}
            searchTitles={searchTitles}
            titleInput={titleInput}
            onTitleInputChange={setTitleInput}
            onTitleInputKeyDown={(event) => {
              if (isSearchConfirmShortcut(event)) {
                return;
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                addTitle();
              }
            }}
            onAddTitle={addTitle}
            onRemoveTitle={(title) => {
              setSearchTitles(searchTitles.filter((candidate) => candidate !== title));
            }}
            searchCompanies={searchCompanies}
            companyInput={companyInput}
            onCompanyInputChange={setCompanyInput}
            onCompanyInputKeyDown={(event) => {
              if (isSearchConfirmShortcut(event)) {
                return;
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                addCompany();
              }
            }}
            onAddCompany={addCompany}
            onRemoveCompany={(company) => {
              setSearchCompanies(searchCompanies.filter((candidate) => candidate !== company));
            }}
            onSearchSubmit={handleLookupSubmit}
            onApplySearchHistory={(params) => {
              const titles = params.titles || [];
              const companies = params.companies || [];

              setSearchKeyword(params.title || '');
              setSearchLocation(params.location || '');
              setSearchTitles(titles);
              setTitleInput('');
              setSearchCompanies(companies);
              setCompanyInput('');

              void handleSearch({
                title: params.title,
                location: params.location,
                titles,
                companies,
                page: 1,
              });
            }}
            onCancelSearch={cancelSearch}
            onClear={() => {
              cancelSearch();
              setSearchKeyword('');
              setSearchLocation('');
              setSearchTitles([]);
              setTitleInput('');
              setSearchCompanies([]);
              setCompanyInput('');
              setSearchResults([]);
              setSearchTotal(0);
              setSearchError(null);
              router.replace(basePath, { scroll: true });
            }}
            isSearching={isSearchLoading}
            searchProgress={searchProgress}
            className="border border-border bg-card -mx-4 sm:mx-0"
          />
        </motion.div>

        <SectionPanel
          icon={Briefcase}
          title="Search Results"
          description="Jobs matching your search criteria."
          contentClassName="p-3 sm:p-4 space-y-3"
        >
          <SectionHeader
            level="section"
            title="Matching jobs"
            icon={Briefcase}
            count={jobsCountLabel}
            trailing={<ViewModeToggle value={viewMode} onChange={setViewMode} />}
          />

          <div ref={searchResultsTopRef} className="scroll-mt-28" />

          {searchError && (
            <Alert variant="destructive" className="border">
              <AlertDescription>{searchError}</AlertDescription>
            </Alert>
          )}

          {isSearchLoading && (
            <div className="flex items-center justify-center py-12">
              <MainLoader title="Searching jobs" subtitle="Finding matching positions..." size="md" />
            </div>
          )}

          {!isSearchLoading && searchResults.length === 0 && !searchError && (
            <div className="p-4 border border-border bg-card text-sm text-muted-foreground text-center">
              {searchKeyword || searchLocation || searchTitles.length > 0 || searchCompanies.length > 0 ? (
                'No jobs found matching your search criteria. Try different keywords or location.'
              ) : (
                'Enter a job title, location, or company to start searching.'
              )}
            </div>
          )}

          {!isSearchLoading && searchResults.length > 0 && (
            <JobsList
              jobs={searchResults}
              viewMode={viewMode}
              detailBasePath={detailBasePath}
              itemKeyPrefix="search"
            />
          )}

          {!isSearchLoading && shouldShowPagination && (
            <PaginatorBar
              page={searchPage}
              totalPages={searchTotal > 0 ? Math.ceil(searchTotal / JOBS_PER_PAGE) : searchPage + 1}
              onPageChange={handleSearchPageChange}
              isLoading={isSearchLoading}
              size="sm"
            />
          )}
        </SectionPanel>
      </Container>
    </div>
  );
}
